import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBrowserSearch } from '@/hooks/useBrowserSearch';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { SeoHead } from '@/components/SeoHead';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { ReadWithAI } from '@/components/ReadWithAI';
import { Button } from '@/components/ui/button';
import { buildSignupUrl } from '@/lib/utm';
import { trackLandingCtaClicked } from '@/lib/analytics';
import {
  EWIDENCJA_ADVISORY_RATES,
  EWIDENCJA_ART12,
  EWIDENCJA_CHECKLIST,
  EWIDENCJA_COLUMN_NOTE,
  EWIDENCJA_COLUMNS,
  EWIDENCJA_CTA,
  EWIDENCJA_DESCRIPTION,
  EWIDENCJA_DISCLAIMER,
  EWIDENCJA_FAQ,
  EWIDENCJA_H1,
  EWIDENCJA_INTERNAL_LINKS,
  EWIDENCJA_LEAD,
  EWIDENCJA_LIMIT,
  EWIDENCJA_NAV_LABEL,
  EWIDENCJA_PREPARE,
  EWIDENCJA_PRICE,
  EWIDENCJA_REVIEWED,
  EWIDENCJA_ROWS,
  EWIDENCJA_SEO_TITLE,
  EWIDENCJA_SOURCES,
  ewidencjaUrl,
} from '@/content/blog/ewidencja-przychodow';

const REVIEW_DATE_FORMAT = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function formatReviewDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? isoDate : REVIEW_DATE_FORMAT.format(parsed);
}

function useDraftSignup(href: string) {
  const search = useBrowserSearch();
  return useMemo(() => (search === null ? href : buildSignupUrl(href, search)), [href, search]);
}

function CtaPair({
  primaryHref,
  primaryLabel,
  location,
}: {
  primaryHref: string;
  primaryLabel: string;
  location: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <Button asChild className="rounded-xl font-semibold min-h-11">
        <a href={primaryHref} onClick={() => trackLandingCtaClicked(location, primaryLabel)}>
          {primaryLabel}
          <ArrowRight className="size-4" aria-hidden="true" />
        </a>
      </Button>
    </div>
  );
}

const EwidencjaPrzychodowRyczalt2026 = () => {
  const canonical = ewidencjaUrl();
  const leadHref = useDraftSignup(EWIDENCJA_CTA.lead.href);
  const tableHref = useDraftSignup(EWIDENCJA_CTA.table.href);
  const footerHref = useDraftSignup(EWIDENCJA_CTA.footer.href);
  const stickyHref = useDraftSignup(EWIDENCJA_CTA.sticky.href);

  return (
    <div className="min-h-screen pt-16 md:pt-20 pb-24 md:pb-0">
      <SeoHead>
        <html lang="pl" />
        <title>{EWIDENCJA_SEO_TITLE}</title>
        <meta name="description" content={EWIDENCJA_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={EWIDENCJA_SEO_TITLE} />
        <meta property="og:description" content={EWIDENCJA_DESCRIPTION} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="pl_PL" />
      </SeoHead>

      <Navigation />

      <article className="bg-background">
        <nav
          aria-label="Ścieżka nawigacji"
          className="max-w-3xl mx-auto px-6 md:px-8 pt-10 text-sm text-muted-foreground"
        >
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Bookeper
              </Link>
            </li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            <li>
              <a href="/blog" className="hover:text-primary transition-colors">
                Blog
              </a>
            </li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="text-foreground">
              {EWIDENCJA_NAV_LABEL}
            </li>
          </ol>
        </nav>

        <header className="max-w-3xl mx-auto px-6 md:px-8 pt-6 pb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
            {EWIDENCJA_H1}
          </h1>
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed">{EWIDENCJA_LEAD}</p>
          <p className="mt-5 text-sm md:text-base text-foreground/80 leading-relaxed">
            {EWIDENCJA_LIMIT}
          </p>
          <p className="mt-6 text-sm text-muted-foreground">
            Ostatnia weryfikacja faktów:{' '}
            <time dateTime={EWIDENCJA_REVIEWED}>{formatReviewDate(EWIDENCJA_REVIEWED)}</time>
          </p>
        </header>

        <div className="max-w-3xl mx-auto px-6 md:px-8 pb-8 space-y-4">
          <CtaPair
            primaryHref={leadHref}
            primaryLabel={EWIDENCJA_CTA.lead.label}
            location="lead_inline"
          />
          <p className="text-sm text-muted-foreground">{EWIDENCJA_PREPARE}</p>
          <p className="text-sm text-foreground/80">{EWIDENCJA_PRICE}</p>
        </div>

        <ReadWithAI page="ewidencja-przychodow" />

        <div className="max-w-3xl mx-auto px-6 md:px-8 pb-8 space-y-12">
          <section id="co-powinna-dawac" aria-labelledby="co-powinna-dawac-heading">
            <h2
              id="co-powinna-dawac-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Co powinna dawać dobra ewidencja
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-4">
              Dobrze prowadzona ewidencja ma odpowiadać na trzy pytania:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-base text-foreground/80">
              <li>Jaki przychód powstał w danym miesiącu.</li>
              <li>Z jakiego dokumentu wynika (faktura, korekta, inny dokument).</li>
              <li>
                Do którego procesu rozliczeniowego trafi dalej (miesięczny / kwartalny ryczałt, PIT
                roczny).
              </li>
            </ol>
            <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed">
              Jeśli po miesiącu nadal przeszukujesz maile i konto bankowe, ewidencja nie jest
              jeszcze procesem — jest gaszeniem pożaru.
            </p>
          </section>

          <section id="minimalny-proces" aria-labelledby="minimalny-proces-heading">
            <h2
              id="minimalny-proces-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Minimalny proces miesięczny
            </h2>
            <h3 className="text-xl font-semibold mb-3">
              1. Zbierz komplet dokumentów przychodowych
            </h3>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-3">
              Na koniec tygodnia albo miesiąca miej listę:
            </p>
            <ul className="space-y-2.5 mb-8">
              {[
                'wystawionych faktur',
                'korekt',
                'innych dokumentów wpływających na przychód',
                'wpływów, które trzeba powiązać z dokumentami',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-base text-foreground/80">
                  <Check className="size-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold mb-3">2. Zapisuj przychód na bieżąco</h3>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-3">
              Prosty rytm działa lepiej niż „hurtem przed terminem ryczałtu”:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-base text-foreground/80 mb-8">
              <li>Wystawiasz dokument.</li>
              <li>Od razu zapisujesz go w ewidencji.</li>
              <li>Na koniec miesiąca robisz kontrolę — nie odtwarzasz historii od zera.</li>
            </ol>

            <h3 className="text-xl font-semibold mb-3">3. Domknij miesiąc checklistą</h3>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-3">
              Przed wyliczeniem należnego ryczałtu sprawdź:
            </p>
            <ul className="space-y-2.5">
              {EWIDENCJA_CHECKLIST.map((item) => (
                <li key={item} className="flex gap-3 text-base text-foreground/80">
                  <span
                    className="mt-0.5 size-5 shrink-0 rounded border border-border"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="wzor-ewidencji" aria-labelledby="wzor-ewidencji-heading">
            <h2
              id="wzor-ewidencji-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Wzór ewidencji (kolumny ustawowe)
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-5">
              Wzór kolumn wynika z załącznika do rozporządzenia Ministra Finansów i Gospodarki z 6
              września 2025 r. (Dz.U. 2025 poz. 1294) — obowiązuje od 1 stycznia 2026 r. To
              obowiązkowy układ ewidencji, nie „dowolny szablon Excela”.
            </p>
            <div className="overflow-x-auto rounded-xl border border-foreground/20 bg-card">
              <table className="min-w-[72rem] w-full text-xs md:text-sm font-mono tabular-nums">
                <caption className="sr-only">
                  Przykładowe wpisy ewidencji przychodów za marzec 2026 według kolumn z Dz.U. 2025
                  poz. 1294
                </caption>
                <thead>
                  <tr className="border-b border-border bg-background-alt text-left">
                    {EWIDENCJA_COLUMNS.map((column) => (
                      <th
                        key={column}
                        scope="col"
                        className="px-2 py-2 font-semibold whitespace-nowrap"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {EWIDENCJA_ROWS.map((row) => (
                    <tr key={row.lp} className="border-b border-border/60 last:border-0">
                      <td className="px-2 py-2">{row.lp}</td>
                      <td className="px-2 py-2">{row.dateIn}</td>
                      <td className="px-2 py-2">{row.dateEarned}</td>
                      <td className="px-2 py-2">{row.ksef}</td>
                      <td className="px-2 py-2">{row.doc}</td>
                      <td className="px-2 py-2">{row.nip}</td>
                      <td className="px-2 py-2">{row.rate17}</td>
                      <td className="px-2 py-2">{row.rate15}</td>
                      <td className="px-2 py-2">{row.rate14}</td>
                      <td className="px-2 py-2">{row.rate125}</td>
                      <td className="px-2 py-2">{row.rate12}</td>
                      <td className="px-2 py-2">{row.rate10}</td>
                      <td className="px-2 py-2">{row.rate85}</td>
                      <td className="px-2 py-2">{row.rate55}</td>
                      <td className="px-2 py-2">{row.rate3}</td>
                      <td className="px-2 py-2">{row.total}</td>
                      <td className="px-2 py-2 font-sans">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 space-y-3 text-base text-foreground/80 leading-relaxed">
              <p>
                Kwoty w przykładzie: netto (czynny VAT). Przy zwolnieniu z VAT wpisujesz kwotę z
                dokumentu. Korekta idzie tą samą stawką co korygowana faktura.
              </p>
              <p>Zasada stawek (Product VERIFY):</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>{EWIDENCJA_ADVISORY_RATES}</li>
                <li>
                  Najem: <strong>8,5%</strong> do 100 000 zł przychodu z najmu,{' '}
                  <strong>12,5%</strong> powyżej.
                </li>
                <li>
                  IT / oprogramowanie <strong>12%</strong> tylko dla PKWiU z listy ustawowej — nie
                  „każde IT”.
                </li>
                <li>Bez rozdzielenia przychodów na stawki: {EWIDENCJA_ART12}.</li>
              </ul>
              <p className="text-sm text-muted-foreground">{EWIDENCJA_COLUMN_NOTE}</p>
              <p>Zasada porządku: trzymaj jeden układ nazw, dat i numerów od początku roku.</p>
            </div>
            <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-3">
              <CtaPair
                primaryHref={tableHref}
                primaryLabel={EWIDENCJA_CTA.table.label}
                location="wzor_table"
              />
              <p className="text-sm text-foreground/80">{EWIDENCJA_CTA.table.microcopy}</p>
            </div>
          </section>

          <section id="ewidencja-a-rozliczenie" aria-labelledby="ewidencja-a-rozliczenie-heading">
            <h2
              id="ewidencja-a-rozliczenie-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Jak połączyć ewidencję z rozliczeniem
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-3">
              Ewidencja przychodów nie jest osobnym bytem. Powinna zasilać:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-base text-foreground/80">
              <li>wyliczenie należnego ryczałtu (miesięcznie lub kwartalnie),</li>
              <li>roczne rozliczenie PIT,</li>
              <li>checklistę zamknięcia roku.</li>
            </ul>
            <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed">
              Dlatego warto trzymać jeden układ od stycznia do grudnia — niezależnie od tego, czy
              pracujesz w arkuszu (z zastrzeżeniem JPK_EWP poniżej), czy w aplikacji.
            </p>
            <ul className="mt-5 space-y-2">
              {EWIDENCJA_INTERNAL_LINKS.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="text-foreground underline underline-offset-4 hover:text-primary"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section id="najczestsze-bledy" aria-labelledby="najczestsze-bledy-heading">
            <h2
              id="najczestsze-bledy-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Najczęstsze błędy
            </h2>
            <ol className="list-decimal pl-5 space-y-2 text-base md:text-lg text-foreground/80">
              <li>Wpisy raz na kilka miesięcy — rośnie ryzyko braków i pomyłek.</li>
              <li>Korekty bez opisu albo ze złą stawką — szybko psują sumę miesiąca.</li>
              <li>Brak podziału per stawka — ryzyko {EWIDENCJA_ART12}.</li>
              <li>Pięć równoległych list zamiast obowiązkowego wzoru kolumn.</li>
              <li>Ryczałt liczony z niezamkniętej ewidencji — cały miesiąc na zgadywaniu.</li>
            </ol>
          </section>

          <section id="od-czego-zaczac" aria-labelledby="od-czego-zaczac-heading">
            <h2
              id="od-czego-zaczac-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              Od czego zacząć na ryczałcie
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              Jeśli dopiero przechodzisz na ryczałt: ustaw proces ewidencji (kolumny z
              rozporządzenia) przed pierwszym miesiącem, potem policz należny ryczałt w
              kalkulatorze. Nie zaczynaj od „jakoś to będzie w Excelu w kwietniu”.
            </p>
          </section>
        </div>

        <section aria-labelledby="faq-heading" className="max-w-3xl mx-auto px-6 md:px-8 py-12">
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
            Najczęstsze pytania
          </h2>
          <div className="space-y-6">
            {EWIDENCJA_FAQ.map((item) => (
              <div key={item.q} className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.q}</h3>
                <p className="text-base text-foreground/80 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="bookeper-heading"
          className="max-w-3xl mx-auto px-6 md:px-8 pb-12"
        >
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 space-y-4">
            <h2 id="bookeper-heading" className="text-xl font-semibold">
              {EWIDENCJA_CTA.footer.heading}
            </h2>
            <p className="text-base text-foreground/80 leading-relaxed">
              {EWIDENCJA_CTA.footer.body}
            </p>
            <CtaPair
              primaryHref={footerHref}
              primaryLabel={EWIDENCJA_CTA.footer.label}
              location="footer"
            />
          </div>
        </section>

        <section aria-labelledby="sources-heading" className="max-w-3xl mx-auto px-6 md:px-8 pb-20">
          <h2
            id="sources-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3"
          >
            Na czym opieramy daty i liczby
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
            {EWIDENCJA_SOURCES.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">{EWIDENCJA_DISCLAIMER}</p>
        </section>
      </article>

      <div className="fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur-sm px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-lg md:hidden">
        <p className="text-sm font-medium mb-2">{EWIDENCJA_CTA.sticky.line}</p>
        <Button asChild className="w-full rounded-xl font-semibold min-h-11">
          <a
            href={stickyHref}
            onClick={() => trackLandingCtaClicked('sticky_cta', EWIDENCJA_CTA.sticky.label)}
          >
            {EWIDENCJA_CTA.sticky.label}
          </a>
        </Button>
      </div>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default EwidencjaPrzychodowRyczalt2026;
